import { useStrings } from "@1js/localization-components";
import {
  PromptBadges,
  usePromptFilterLocalizations,
  type Badge,
} from "@1js/pl-card-components";
import {
  Text,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  useId,
} from "@fluentui/react-components";
import { Info16Regular } from "@fluentui/react-icons";
import * as React from "react";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { useHandoffService } from "../../hooks/useHandoffService";
import type { WebPrompt } from "../data";
import {
  DetailDisplayCategoryDescription,
  DetailProductDescription,
  DetailProductDescriptionAlsoWorksIn,
  DetailProductDescriptionAlsoWorksInInfoLabel,
} from "./PromptDescription.web.string";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    alignSelf: "stretch",
    justifyContent: "space-between",
    ...shorthands.gap("8px"),
    "@media (max-width: 540px)": {
      flexDirection: "column",
    },
  },

  title: {
    color: tokens.colorNeutralForeground2,
    fontSize: "0.75rem",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "1.375rem",
  },

  category: {
    color: tokens.colorNeutralForeground2,
    fontSize: "0.75rem",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "1.375rem",
    alignContent: "end",
  },

  badge: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    alignSelf: "stretch",
    ...shorthands.gap("8px"),
  },
  visibleTooltipIcon: {
    color: tokens.colorNeutralForeground2BrandSelected,
  },
  alsoWorksInTitleContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  tooltipIcon: {
    marginLeft: "4px",
    height: "14px",
    width: "14px",
  },
});

type PromptDescriptionProps = {
  prompt: WebPrompt;
};

export const PromptDescription: React.FC<PromptDescriptionProps> = ({
  prompt,
}) => {
  const styles = useStyles();
  const localizations = useStrings({
    DetailProductDescription,
    DetailProductDescriptionAlsoWorksIn,
    DetailProductDescriptionAlsoWorksInInfoLabel,
    DetailDisplayCategoryDescription,
  });

  const { isTryInOnAuthed } = useFeatureEnablement();
  const { facetValueToLabel } = usePromptFilterLocalizations();

  const badges: Badge[] = prompt.Products.map((product) => {
    return {
      id: product,
    };
  });

  const { isHandoffSupported } = useHandoffService();

  const tryInBadgesCount = prompt.Products.filter((bade) =>
    isHandoffSupported(bade)
  ).length;
  const tryInByCopyBadges: Badge[] = prompt.Products.filter(
    (badge) => !isHandoffSupported(badge)
  ).map((product) => {
    return {
      id: product,
    };
  });

  const displayCategoryBadges: Badge[] = [
    {
      id: facetValueToLabel(prompt.DisplayCategory),
    },
  ];

  const alsoWorksInTooltipContentId = useId("AlsoWorksInTooltipContent");
  const [isAlsoWorksInTooltipVisible, setAlsoWorksInTooltipVisible] =
    React.useState(false);

  return (
    <div className={styles.container}>
      {!isTryInOnAuthed && (
        <div className={styles.badge}>
          <Text className={styles.title}>
            {localizations.DetailProductDescription()}
          </Text>
          <PromptBadges
            badges={badges}
            maxNumber={5}
            displayMode="iconAndText"
          />
        </div>
      )}
      {isTryInOnAuthed && tryInByCopyBadges.length > 0 && (
        <div className={styles.badge}>
          <div
            aria-owns={
              isAlsoWorksInTooltipVisible
                ? alsoWorksInTooltipContentId
                : undefined
            }
            className={styles.alsoWorksInTitleContainer}
          >
            <Text className={styles.title}>
              {tryInBadgesCount === 0
                ? localizations.DetailProductDescription()
                : localizations.DetailProductDescriptionAlsoWorksIn()}
            </Text>
            <Tooltip
              content={{
                children:
                  localizations.DetailProductDescriptionAlsoWorksInInfoLabel(),
                id: alsoWorksInTooltipContentId,
              }}
              positioning="above-start"
              withArrow
              relationship="label"
              onVisibleChange={(_, data) =>
                setAlsoWorksInTooltipVisible(data.visible)
              }
            >
              <Info16Regular
                tabIndex={0}
                className={mergeClasses(
                  styles.tooltipIcon,
                  isAlsoWorksInTooltipVisible && styles.visibleTooltipIcon
                )}
              />
            </Tooltip>
          </div>
          <PromptBadges
            badges={tryInByCopyBadges}
            maxNumber={5}
            displayMode="iconAndText"
          />
        </div>
      )}
      {!prompt.IsUserPrompt && prompt.Origin !== "User" && (
        <div className={styles.badge}>
          <Text className={styles.category}>
            {localizations.DetailDisplayCategoryDescription()}
          </Text>
          <PromptBadges
            badges={displayCategoryBadges}
            maxNumber={5}
            displayMode="textOnly"
          />
        </div>
      )}
    </div>
  );
};
